import { createApp } from 'vue'
import './polyfills'
import * as Sentry from '@sentry/vue'
import App from '@/App.vue'
import { registerPlugins } from '@core/utils/plugins'
import { router } from '@/plugins/1.router'
import './echo'

import '@core-scss/template/index.scss'
import '@styles/styles.scss'

// Create vue app
const app = createApp(App)

/**
 * Публичные ссылки несут одноразовый секрет прямо в URL: `/c/<token>` (страница кандидата
 * подбора) и `/api/recruiting/links/<token>[/start]` (её API), `/x/<code>` (загрузочная страница
 * внешнего запуска — и она же адрес обмена кода), `/r/<code>` (короткая ссылка на разбор) и
 * `external-review/{launch}?signature=…` (сам разбор — право на странице одно, и это подпись).
 * Секрет — единственное, что даёт доступ к интервью, к игре запуска или к разбору, поэтому он не
 * должен попадать в Sentry ни в каком виде события. Редакция — в одном месте, вызывается из всех
 * хуков ниже, и повторяет серверную (`Modules\Shared\Support\SecretLinkUrlRedactor`): секрет
 * уезжает двумя независимыми путями, и чинить надо оба.
 */
function scrubSecretLinkUrl(url: string | undefined): string | undefined {
  if (!url)
    return url

  const withoutPageToken = url
    .replace(/\/c\/[^/?#]+/gi, '/c/[redacted]')
    .replace(/\/x\/[^/?#]+/gi, '/x/[redacted]')
    .replace(/\/r\/[^/?#]+/gi, '/r/[redacted]')

  const withoutLinkToken = withoutPageToken.replace(
    /\/api\/recruiting\/links\/[^/?#]+(\/start)?/gi,
    (_match: string, startSuffix?: string) => `/api/recruiting/links/[redacted]${startSuffix ?? ''}`,
  )

  // Подпись — секрет в query-строке, а не в пути: страница разбора живёт по ней целиком, и
  // regexp по сегментам пути её не ловит.
  return withoutLinkToken.replace(/\bsignature=[^&#]*/gi, 'signature=[redacted]')
}

function scrubBreadcrumbUrls(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (typeof breadcrumb.message === 'string')
    breadcrumb.message = scrubSecretLinkUrl(breadcrumb.message)

  if (breadcrumb.data) {
    for (const key of ['url', 'to', 'from'] as const) {
      if (typeof breadcrumb.data[key] === 'string')
        breadcrumb.data[key] = scrubSecretLinkUrl(breadcrumb.data[key])
    }
  }

  return breadcrumb
}

/**
 * Атрибуты, значение которых само является секретом, а не URL с секретом внутри.
 * `@sentry/vue` кладёт в атрибуты корневого спана и параметры маршрута (`params.<name>`), и
 * параметры query-строки (`query.<name>`) — см. build/esm/router.js. То есть на `/c/[token]`,
 * `/x/[code]` и на подписанном адресе разбора туда попадает ГОЛЫЙ секрет: regexp по URL его не
 * ловит, потому что ни префикса `/c/`, ни `signature=` рядом нет.
 */
const SECRET_SPAN_ATTRIBUTES = new Set(['params.token', 'params.code', 'query.signature'])

function scrubSpanData(data: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(data)) {
    if (typeof value !== 'string')
      continue

    data[key] = SECRET_SPAN_ATTRIBUTES.has(key) ? '[redacted]' : scrubSecretLinkUrl(value)
  }
}

// Скрабит event.request.url, имя транзакции, breadcrumbs, данные корневого спана
// (contexts.trace.data) и дочерние spans (fetch/xhr-инструментация browserTracingIntegration
// кладёт полный URL запроса в span.description и span.data['http.url'] — см. @sentry/browser
// tracing/request.js).
function scrubSentryEvent<T extends Sentry.Event>(event: T): T {
  if (event.request?.url)
    event.request.url = scrubSecretLinkUrl(event.request.url)

  if (event.transaction)
    event.transaction = scrubSecretLinkUrl(event.transaction)

  if (event.breadcrumbs)
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumbUrls)

  // Атрибуты корневого спана уезжают в транзакцию отдельным полем: spanToTransactionTraceContext()
  // включает `data` в contexts.trace (в отличие от spanToTraceContext() для error-событий, где
  // data отброшен) — см. @sentry/core utils/spanUtils.js. Без этой ветки токен из `params.token`
  // отправлялся бы в открытом виде на каждой засэмплированной загрузке страницы кандидата.
  if (event.contexts?.trace?.data)
    scrubSpanData(event.contexts.trace.data)

  if (event.spans) {
    event.spans = event.spans.map(span => {
      if (span.description)
        span.description = scrubSecretLinkUrl(span.description)

      if (span.data)
        scrubSpanData(span.data)

      return span
    })
  }

  return event
}

/**
 * Session Replay пишет текущий URL страницы прямо в DOM-снапшот (rrweb meta-событие) —
 * это НЕ проходит через `beforeSend`/`beforeSendTransaction` (SDK не вызывает их для
 * `replay_event`, см. @sentry/core baseclient.js: и isErrorEvent, и isTransactionEvent
 * возвращают false для type='replay_event') и не покрывается `beforeAddRecordingEvent`
 * (тот скрабит только кастомные recording-события вроде breadcrumbs, а не DOM-снапшоты
 * низкоуровневого rrweb-рекордера — см. @sentry-internal/replay JSDoc). Значит URL со
 * страницы кандидата и страницы внешнего запуска настроить безопасно нельзя — единственный
 * вариант: не писать Replay на этих роутах вообще. И кандидат, и ученик внешнего запуска всегда
 * попадают на свою страницу прямой ссылкой (полная перезагрузка) — переходов туда из остального
 * SPA в коде нет, поэтому решения по `window.location.pathname` в момент Sentry.init достаточно.
 *
 * `external-review` в списке по той же причине, что `/c/` и `/x/`, хотя секрет у него лежит не
 * в пути, а в query-строке: rrweb пишет в Meta-событие `window.location.href` ЦЕЛИКОМ, вместе
 * с `?expires=…&signature=…`. Остальные каналы там уже закрыты — и fetch-breadcrumb, и
 * `query.signature` в атрибутах спана, — а Replay мимо `beforeSend` проходит.
 */
const isSecretLinkPath = (path: string): boolean => /^\/(?:c|x|external-review)\//.test(path)

if (import.meta.env.VITE_SENTRY_IS_ENABLED === 'true') {
  console.log('Sentry is enabled')

  const tracePropagationTargets = (import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS ?? '')
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean)

  const integrations = [
    Sentry.browserTracingIntegration({ router }),
  ]

  if (!isSecretLinkPath(window.location.pathname))
    integrations.push(Sentry.replayIntegration())

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_RELEASE_NAME,

    // Только абсолютный адрес: isSentryRequestUrl в SDK сравнивает его с URL запроса как точную строку,
    // и Replay сверяет с ним абсолютные URL (@sentry/core/build/esm/utils/isSentryRequestUrl.js).
    tunnel: import.meta.env.VITE_SENTRY_TUNNEL ? `${window.location.origin}${import.meta.env.VITE_SENTRY_TUNNEL}` : undefined,
    initialScope: {
      tags: { region: import.meta.env.VITE_SENTRY_REGION },
    },
    integrations,
    tracePropagationTargets,
    ignoreErrors: [
      'Failed to fetch dynamically imported module',
      'Unable to preload CSS',
      'WeakMap keys must be objects or non-registered symbols',

      // Browser extension errors (password managers, etc.)
      /Error invoking.*Method not found/,
    ],

    tracesSampleRate: Number.parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
    replaysSessionSampleRate: Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? '0.1'),
    replaysOnErrorSampleRate: Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? '1.0'),

    beforeSend: event => scrubSentryEvent(event),
    beforeSendTransaction: event => scrubSentryEvent(event),
    beforeBreadcrumb: breadcrumb => scrubBreadcrumbUrls(breadcrumb),
  })

  // Защита от гипотетической клиентской (без перезагрузки) навигации на `/c/<token>` и
  // `/x/<code>` — если Replay всё же активен на этом роуте, глушим запись.
  router.beforeEach(to => {
    if (isSecretLinkPath(to.path))
      Sentry.getReplay()?.stop()
  })
}

// Register plugins
registerPlugins(app)

// Mount vue app
app.mount('#app')
